import { lazy } from "react";

/**
 * Wraps a lazy import with retry logic for handling stale chunks after deploys.
 *
 * Strategy: On first failure, retry the original import up to 3 times with
 * exponential backoff. If all retries fail, hard-reload the page to get
 * the latest build assets. We do NOT use cache-busted URLs (e.g. ?t=Date.now())
 * because that can create a separate module evaluation context, leading to
 * duplicate React instances and "Invalid hook call" errors.
 */
export const lazyWithRetries = (importer) => {
    const retryImport = async () => {
        const maxRetries = 3;

        for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                return await importer();
            } catch (error) {
                if (attempt === maxRetries) {
                    // All retries exhausted — hard reload to get latest chunks
                    console.error("Lazy load failed after retries. Reloading page...");
                    window.location.reload();
                    // Return a never-resolving promise to prevent React from
                    // rendering while the page reloads
                    return new Promise(() => {});
                }
                // Exponential backoff: 1s, 2s, 4s
                const delay = 1000 * (2 ** attempt);
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    };

    return lazy(retryImport);
};
